/**
 * Alta de un documento declarado. Lo usan las secciones 2, 3 y 4 del expediente.
 *
 * `NEG-7`: sin entidad **del catálogo** no se puede emitir requerimiento. El
 * campo de texto libre existe para no bloquear el registro mientras la entidad
 * no está dada de alta, y el diálogo lo dice: se puede guardar así, pero no se
 * podrá requerir hasta resolverla.
 */
import { useId, useState } from 'react'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mpfn/ui'
import { Callout, DatePicker, FileUploadField, FormField } from '@mpfn/patterns'
import { useEntidades, useRegistrarDocumento } from '../api/consultas.js'
import { etiquetaDe, useCatalogos } from '../api/catalogos.js'
import type { SeccionVerificable } from '../api/tipos.js'
import { avisarError, avisarExito } from '../lib/avisos.js'
import { hoy } from '../lib/formato.js'

const SIN_ENTIDAD = 'sin-entidad'

interface Props {
  expedienteId: string
  seccion: SeccionVerificable
  abierto: boolean
  onCambio: (abierto: boolean) => void
}

export function DialogoDocumento({ expedienteId, seccion, abierto, onCambio }: Props) {
  const catalogos = useCatalogos()
  const entidades = useEntidades()
  const registrar = useRegistrarDocumento()

  const idDenominacion = useId()
  const idEntidad = useId()
  const idEntidadTexto = useId()
  const idNumero = useId()
  const idFecha = useId()

  const [denominacion, setDenominacion] = useState('')
  const [entidadId, setEntidadId] = useState(SIN_ENTIDAD)
  const [entidadTexto, setEntidadTexto] = useState('')
  const [numeroDocumento, setNumeroDocumento] = useState('')
  const [fechaEmision, setFechaEmision] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)

  function limpiar(): void {
    setDenominacion('')
    setEntidadId(SIN_ENTIDAD)
    setEntidadTexto('')
    setNumeroDocumento('')
    setFechaEmision('')
    setArchivo(null)
  }

  const activas = (entidades.data ?? []).filter((entidad) => entidad.desactivadoEn === undefined)

  return (
    <Dialog
      open={abierto}
      onOpenChange={(valor) => {
        if (!valor) limpiar()
        onCambio(valor)
      }}
    >
      <DialogContent>
        <DialogTitle>Registrar documento declarado</DialogTitle>
        <DialogDescription>
          Sección: {etiquetaDe(catalogos.secciones, seccion)}. Es lo que el trabajador presentó; su
          autenticidad se verifica después.
        </DialogDescription>

        <form
          className="sigfisc-formulario"
          onSubmit={(evento) => {
            evento.preventDefault()
            registrar.mutate(
              {
                expedienteId,
                seccion,
                denominacion: denominacion.trim(),
                ...(entidadId !== SIN_ENTIDAD ? { entidadId } : {}),
                ...(entidadId === SIN_ENTIDAD && entidadTexto.trim()
                  ? { entidadTexto: entidadTexto.trim() }
                  : {}),
                ...(numeroDocumento.trim() ? { numeroDocumento: numeroDocumento.trim() } : {}),
                ...(fechaEmision ? { fechaEmisionDocumento: fechaEmision } : {}),
                ...(archivo ? { archivo } : {}),
              },
              {
                onSuccess: () => {
                  avisarExito('Documento registrado.')
                  limpiar()
                  onCambio(false)
                },
                onError: (error) => {
                  avisarError(error, 'No se pudo registrar el documento')
                },
              },
            )
          }}
        >
          <FormField id={idDenominacion} label="Denominación" required>
            <Input
              value={denominacion}
              autoFocus
              placeholder="Título profesional de Ingeniero Civil"
              onChange={(evento) => {
                setDenominacion(evento.target.value)
              }}
            />
          </FormField>

          <FormField
            id={idEntidad}
            label="Entidad emisora"
            hint="Del catálogo. Sin ella el documento se registra, pero no se puede requerir."
          >
            <Select value={entidadId} onValueChange={setEntidadId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SIN_ENTIDAD}>Todavía no está en el catálogo</SelectItem>
                {activas.map((entidad) => (
                  <SelectItem key={entidad.id} value={entidad.id}>
                    {entidad.siglas ?? entidad.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {entidadId === SIN_ENTIDAD && (
            <>
              <FormField id={idEntidadTexto} label="Entidad (texto libre)">
                <Input
                  value={entidadTexto}
                  placeholder="Nombre tal como figura en el documento"
                  onChange={(evento) => {
                    setEntidadTexto(evento.target.value)
                  }}
                />
              </FormField>
              <Callout tone="info" title="Quedará sin poder requerirse">
                Para emitir un requerimiento hay que resolver la entidad en el catálogo. El
                documento se guarda igual y se puede corregir después.
              </Callout>
            </>
          )}

          <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
            <FormField id={idNumero} label="Número del documento">
              <Input
                value={numeroDocumento}
                onChange={(evento) => {
                  setNumeroDocumento(evento.target.value)
                }}
              />
            </FormField>

            <FormField id={idFecha} label="Fecha de emisión">
              <DatePicker
                id={idFecha}
                value={fechaEmision}
                onChange={setFechaEmision}
                yearRange={{ from: 1960, to: Number(hoy().slice(0, 4)) }}
              />
            </FormField>
          </div>

          <FormField id="legajo" label="PDF del legajo" hint="Lo que el trabajador presentó.">
            <FileUploadField
              id="legajo"
              value={archivo}
              onChange={setArchivo}
              accept=".pdf,image/*"
              acceptLabel="PDF o imagen"
              maxSizeMb={20}
              variant="compact"
              onError={(mensaje) => {
                avisarError(new Error(mensaje), 'El archivo no es válido')
              }}
            />
          </FormField>

          <div className="sigfisc-dialogo__acciones">
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              type="submit"
              loading={registrar.isPending}
              disabled={denominacion.trim() === ''}
            >
              Registrar documento
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
